import { z } from 'zod';

// ---------------------------------------------------------------------------
// MenuItemSchema — G2: canonical fields { slug, sort_order } per api-freeze spec §4.G2
// Note: DB uses numeric IDs; id exposed as number, parent_id as number|null
// ---------------------------------------------------------------------------

// Menu category types present in the catalog. `style`/`featured` are real
// menu=true category types in the DB (see migration 20260508000006); the frozen
// /v1/ contract was originally authored with only the first three and rejected
// the rest at serialization (500 on /v1/menu). Widening is additive/backward-
// compatible — existing consumers handling the first three are unaffected.
// `theme`/`room`/`kids` added 2026-06-13 — unblocks selectHubCategories(menu,'theme')
// returning real DB categories (was returning 0 due to validType() degradation).
// `color`/`hub` added 2026-06-18 — WS1-T13: complete the cat_type enum coverage.
export const MENU_ITEM_TYPES = ['category', 'combi', 'static', 'style', 'featured', 'theme', 'room', 'kids', 'color', 'hub'] as const;

// ---------------------------------------------------------------------------
// CombiSlotMapSchema — WS1-T13: additive field on GET /v1/categories.
//
// Models categories.combi JSONB: { theme?, kids?, color?, style?, room?, ... }.
// Keys are combi-slot names; values are category-id integers.
// Uses z.record (open-ended) rather than a closed object so future hub axes
// (e.g. `audience`) round-trip without a schema change (forward-compatible).
// Unknown extra keys are stripped on output (.strip()) rather than rejected,
// which keeps the contract additive in both directions.
// ---------------------------------------------------------------------------
export const CombiSlotMapSchema = z.record(
  z.string(),
  z.number().int().positive(),
).optional().default({});
export type CombiSlotMap = z.infer<typeof CombiSlotMapSchema>;

export const MenuItemSchema = z.object({
  id: z.number().int().positive(),
  type: z.enum(MENU_ITEM_TYPES),
  name: z.string(),
  slug: z.string(),
  sort_order: z.number().int(),
  parent_id: z.number().int().nullable(),
  active: z.boolean(),
  // D4: cover image Storage key (NULL until admin populates categories.preview).
  preview: z.string().nullable().optional(),
  // D4: denormalized wallpaper count (trigger-updated in DB; 0 until populated).
  wallpaper_count: z.number().int().nonnegative().optional().default(0),
  // WS1-T13: additive fields for taxonomy dispatch + sitemap + admin guard.
  // Both optional (not required) so older callers (getMenu) that build
  // MenuItem directly remain valid TypeScript without listing these fields.
  // is_indexable=undefined means "not yet fetched" — callers treat absent as true.
  combi: CombiSlotMapSchema.optional(),
  is_indexable: z.boolean().optional(),
  // WS2: per-entity SEO override fields from categories_i18n.
  // Optional + nullable so existing callers and /v1/ contract remain additive-backward-compatible.
  seo_title: z.string().nullable().optional(),
  seo_h1: z.string().nullable().optional(),
  seo_description: z.string().nullable().optional(),
  // WS3/A2: category content fields from categories_i18n.
  // Additive + optional so older cached responses validate without these fields.
  seo_text: z.string().nullable().optional(),
  faq: z
    .array(z.object({ question: z.string(), answer: z.string() }))
    .nullable()
    .optional()
    .default([]),
});
export type MenuItem = z.infer<typeof MenuItemSchema>;

// ---------------------------------------------------------------------------
// ListMenuRequestSchema / ListMenuResponseSchema — GET /v1/menu
// G2: response wrapped in { categories: [...] }
// ---------------------------------------------------------------------------

export const ListMenuRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  site_code: z.string().min(1).optional(),
});
export type ListMenuRequest = z.infer<typeof ListMenuRequestSchema>;

export const ListMenuResponseSchema = z.object({
  categories: z.array(MenuItemSchema),
});
export type ListMenuResponse = z.infer<typeof ListMenuResponseSchema>;

// ---------------------------------------------------------------------------
// VariantSummarySchema — listing card
// ---------------------------------------------------------------------------

export const VariantSummarySchema = z.object({
  wallpaper_id: z.number().int().positive(),
  // category_id is optional post-T5: listings now return per-wallpaper rows from
  // wallpapers_i18n (no per-category variant); old cached responses may still carry it.
  category_id: z.number().int().positive().optional(),
  lang_code: z.string().optional(),
  // slug: uri from wallpapers_i18n (canonical per-wallpaper slug, no category context).
  slug: z.string(),
  name: z.string(),
  alt: z.string().nullable(),
  og_image_url: z.string().url().nullable().optional(),
});
export type VariantSummary = z.infer<typeof VariantSummarySchema>;

// ---------------------------------------------------------------------------
// VariantDetailSchema — G4: extended with wallpaper + category nested objects
// ---------------------------------------------------------------------------

export const VariantDetailSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  category_id: z.number().int().positive(),
  lang_code: z.string(),
  slug: z.string(),
  name: z.string(),
  alt: z.string().nullable(),
  seo_title: z.string().nullable(),
  seo_h1: z.string().nullable(),
  seo_description: z.string().nullable(),
  seo_text: z.string().nullable(),
  generated_at: z.string().datetime().nullable(),
  // G4: new embedded objects for OG image + metadata
  wallpaper: z.object({
    width_px: z.number().int(),
    height_px: z.number().int(),
    original_path: z.string().nullable(),
  }),
  category: z.object({
    name: z.string(),
    seo_title: z.string().nullable(),
  }),
});
export type VariantDetail = z.infer<typeof VariantDetailSchema>;

// ---------------------------------------------------------------------------
// ListCategoryRequestSchema / ListCategoryResponseSchema
// GET /v1/categories/:slug?lang=pl&page=1&perPage=24
// ---------------------------------------------------------------------------

export const ListCategoryRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  site_code: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24),
  // 'popular': rank by wallpaper_order_stats.orders_count (2026-06-25-wandero-catalog-sort-by-order-count).
  sort: z.enum(['default', 'popular']).default('default'),
});
export type ListCategoryRequest = z.infer<typeof ListCategoryRequestSchema>;

export const ListCategoryResponseSchema = z.object({
  items: VariantSummarySchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  perPage: z.number().int().positive(),
});
export type ListCategoryResponse = z.infer<typeof ListCategoryResponseSchema>;

// ---------------------------------------------------------------------------
// GetVariantRequestSchema / GetVariantResponseSchema
// GET /v1/variants/:lang/:category_slug/:variant_slug
// ---------------------------------------------------------------------------

export const GetVariantRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  site_code: z.string().min(1).optional(),
  category_slug: z.string().min(1),
  variant_slug: z.string().min(1),
});
export type GetVariantRequest = z.infer<typeof GetVariantRequestSchema>;

export const GetVariantResponseSchema = VariantDetailSchema;
export type GetVariantResponse = z.infer<typeof GetVariantResponseSchema>;

// ---------------------------------------------------------------------------
// G6: ListCategoriesFlatResponseSchema — GET /v1/categories (flat list, no :slug)
// ---------------------------------------------------------------------------

export const ListCategoriesFlatRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  site_code: z.string().min(1).optional(),
  active_only: z.coerce.boolean().default(true),
});
export type ListCategoriesFlatRequest = z.infer<typeof ListCategoriesFlatRequestSchema>;

export const ListCategoriesFlatResponseSchema = z.object({
  categories: z.array(MenuItemSchema),
});
export type ListCategoriesFlatResponse = z.infer<typeof ListCategoriesFlatResponseSchema>;

// ---------------------------------------------------------------------------
// Onsite Search — GET /v1/search?q=&lang=&page=&perPage=
// ---------------------------------------------------------------------------

/** Request schema for the search endpoint. */
export const SearchRequestSchema = z.object({
  q: z.string().trim().min(1).max(100),
  lang: z.string().min(2).max(10),
  site_code: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24),
});
export type SearchRequest = z.infer<typeof SearchRequestSchema>;

/** Response schema — same item shape as category listings so result cards reuse VariantSummary. */
export const SearchResponseSchema = z.object({
  items: VariantSummarySchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  perPage: z.number().int().positive(),
  query: z.string(), // echo of the normalized q (for the page's "results for …" header)
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

// ---------------------------------------------------------------------------
// WS1-T13: Main-theme write — PATCH /v1/admin/wallpapers/:wallpaper_id/main-theme
//
// Sets is_main_theme=true on the given (wallpaper_id, category_id) join row.
// The api/ layer clears the previous main-theme row first (atomic sequence),
// then the DB trigger acts as the backstop (type='theme' guard).
// ---------------------------------------------------------------------------

export const SetMainThemeRequestSchema = z.object({
  /** Category ID to designate as main theme. Must be a theme-type category. */
  category_id: z.number().int().positive(),
});
export type SetMainThemeRequest = z.infer<typeof SetMainThemeRequestSchema>;

export const SetMainThemeResponseSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  category_id: z.number().int().positive(),
  is_main_theme: z.literal(true),
});
export type SetMainThemeResponse = z.infer<typeof SetMainThemeResponseSchema>;

// ---------------------------------------------------------------------------
// WS1-T13: Combo-exists query — GET /v1/admin/combo-exists
//
// Returns whether a `combi`-type category row with the given slot pair exists.
// Used by admin duplicate-combo guard (§8.1.4).
// Slot values are category-id integers passed as query params.
//
// SECURITY (needs-sec-pass surface):
//   Slot names are interpolated into a PostgREST JSONB column expression
//   `combi->>'<slot>'`. Two-layer guard:
//   1. SLOT_NAME_REGEX — rejects non-[a-z0-9_] characters at input parsing time
//      (belt-and-suspenders: catches injections with quotes, spaces, operators).
//   2. comboSlots() allowlist in api/src/queries/catalog.ts — the authoritative
//      closed check; comboExists() rejects any slot not in the registry.
// ---------------------------------------------------------------------------

// Belt-and-suspenders format guard for slot names.
// Allows only lowercase letters, digits, and underscores (no quotes, no spaces,
// no SQL operators). Must match the spirit of CatTypeSpec.comboSlot values.
const SLOT_NAME_REGEX = /^[a-z_][a-z0-9_]{0,49}$/;

export const ComboExistsRequestSchema = z.object({
  /** Primary L1 slot key (e.g. 'theme', 'kids'). Must be a known combo slot from the taxonomy registry. */
  primary_slot: z.string().regex(SLOT_NAME_REGEX, 'slot name must match /^[a-z_][a-z0-9_]{0,49}$/'),
  /** Primary L1 category ID. */
  primary_id: z.coerce.number().int().positive(),
  /** Facet slot key (e.g. 'room', 'style', 'color'). Must be a known combo slot from the taxonomy registry. */
  facet_slot: z.string().regex(SLOT_NAME_REGEX, 'slot name must match /^[a-z_][a-z0-9_]{0,49}$/'),
  /** Facet category ID. */
  facet_id: z.coerce.number().int().positive(),
  /** Exclude a specific category ID from the check (useful when editing an existing combo). */
  exclude_id: z.coerce.number().int().positive().optional(),
});
export type ComboExistsRequest = z.infer<typeof ComboExistsRequestSchema>;

export const ComboExistsResponseSchema = z.object({
  exists: z.boolean(),
  /** The ID of the existing combo row, if found. Null if not found. */
  existing_id: z.number().int().positive().nullable(),
});
export type ComboExistsResponse = z.infer<typeof ComboExistsResponseSchema>;
